import { Injectable, signal, computed, OnDestroy } from '@angular/core'
import { io, Socket } from 'socket.io-client'
import { environment } from '../../environments/environment'

const NOTIFICATION_SOUND_ENABLED_KEY = 'portalMedico.notificationSoundEnabled'
const NOTIFICATION_SOUND_SRC = 'assets/audio/notification.mp3'

export interface PendingConsultationBrief {
  id: string
  patientFirstName?: string
  patientLastName?: string
  channel?: {
    id?: string
    nameEsEs?: string
    nameEsMx?: string
    nameEn?: string
    namePt?: string
    nameFr?: string
    nameIt?: string
    nameDe?: string
  }
  consultationDate?: number
  consultationReason?: string
}

interface NotificationPayload {
  count: number
  consultations: PendingConsultationBrief[]
}

// URL base del servidor (sin el path de la API
// "http://localhost:3001/v1"  a "http://localhost:3001"
function resolveWsUrl(): string {
  try {
    return new URL(environment.apiUrl).origin
  } catch {
    return environment.apiUrl
  }
}

@Injectable({ providedIn: 'root' })
export class NotificationsWsService implements OnDestroy {
  private socket: Socket | null = null
  private audioElement: HTMLAudioElement | null = null
  private lastPendingCount = 0
  private hasLoadedInitialPendingCount = false

  readonly pendingCount = signal<number>(0)
  readonly pendingList = signal<PendingConsultationBrief[]>([])
  readonly connected = signal<boolean>(false)
  readonly soundEnabled = signal<boolean>(this.readSoundEnabled())

  readonly hasPending = computed(() => this.pendingCount() > 0)

  //muestra "99+" a partir de 100
  readonly badgeLabel = computed(() => {
    const n = this.pendingCount()
    return n > 99 ? '99+' : String(n)
  })

  connect(token: string, subdomain: string): void {
    if (this.socket?.connected) return

    const url = resolveWsUrl()

    this.socket = io(`${url}/notifications`, {
      auth: { token },
      query: { subdomain },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 30_000,
    })

    this.socket.on('connect', () => {
      this.connected.set(true)
    })

    this.socket.on('disconnect', () => {
      this.connected.set(false)
    })

    this.socket.on('connect_error', (err: Error) => {
      console.warn('[NotificationsWs] connect error:', err.message)
      this.connected.set(false)
    })

    this.socket.on('pending_count', (payload: NotificationPayload) => {
      const nextCount = payload?.count ?? 0
      if (this.hasLoadedInitialPendingCount && nextCount > this.lastPendingCount) this.playNotificationSound()
      this.hasLoadedInitialPendingCount = true
      this.lastPendingCount = nextCount
      this.pendingCount.set(nextCount)
      this.pendingList.set(payload?.consultations ?? [])
    })
  }

  setSoundEnabled(enabled: boolean): void {
    this.soundEnabled.set(enabled)
    this.writeSoundEnabled(enabled)
    if (enabled) this.unlockNotificationSound()
  }

  unlockNotificationSound(): void {
    const audio = this.getAudioElement()
    if (!audio) return

    audio.load()
    audio.muted = true
    void audio
      .play()
      .then(() => {
        audio.pause()
        audio.currentTime = 0
        audio.muted = false
      })
      .catch(() => {
        audio.muted = false
      })
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
    }
    this.pendingCount.set(0)
    this.pendingList.set([])
    this.lastPendingCount = 0
    this.hasLoadedInitialPendingCount = false
    this.connected.set(false)
  }

  ngOnDestroy(): void {
    this.disconnect()
    this.audioElement?.pause()
    this.audioElement = null
  }

  private playNotificationSound(): void {
    if (!this.soundEnabled()) return

    const audio = this.getAudioElement()
    if (!audio) return

    audio.pause()
    audio.currentTime = 0
    audio.muted = false
    void audio.play().catch(() => {})
  }

  private getAudioElement(): HTMLAudioElement | null {
    if (typeof window === 'undefined') return null

    try {
      this.audioElement ??= new Audio(NOTIFICATION_SOUND_SRC)
      this.audioElement.preload = 'auto'
      return this.audioElement
    } catch {
      return null
    }
  }

  private readSoundEnabled(): boolean {
    if (typeof window === 'undefined') return true

    try {
      return window.localStorage.getItem(NOTIFICATION_SOUND_ENABLED_KEY) !== 'false'
    } catch {
      return true
    }
  }

  private writeSoundEnabled(enabled: boolean): void {
    if (typeof window === 'undefined') return

    try {
      window.localStorage.setItem(NOTIFICATION_SOUND_ENABLED_KEY, String(enabled))
    } catch {
      // Storage puede estar bloqueado por navegador o política de privacidad.
    }
  }
}
